<template>
  <div class="input-row--toggle">
    <div
      class="form-input--toggle"
      :class="{
        'form-input--toggle--checked': model === true || checked,
        'form-input--toggle--disabled': disabled,
      }"
    >
      <input
        v-show="false"
        :id="name"
        :name="name"
        type="checkbox"
        role="switch"
        class="form-input--toggle--input"
        :checked="(model !== undefined ? model : checked) ?? undefined"
        :disabled="disabled ?? undefined"
        :aria-disabled="disabled ?? undefined"
      />

      <div
        class="form-input--toggle--box"
        role="switch"
        tabindex="0"
        :aria-checked="model === true || checked"
        @click="onClick"
        @keydown.space.prevent="onClick"
      >
        <div class="form-input--toggle--handle"></div>
      </div>
    </div>

    <label v-if="label || $slots.label" :for="`${name}`" @click="onClick">
      <slot name="label">{{ label }}</slot>
    </label>
  </div>
</template>

<script setup lang="ts">
const model = defineModel<boolean>();

const props = defineProps<{
  checked?: boolean;
  label?: string;
  name?: string;
  disabled?: boolean;
}>();

const emit = defineEmits(['click']);

const onClick = () => {
  if (props.disabled === true) {
    return;
  }

  if (model.value !== undefined) {
    model.value = !model.value;
  }

  emit('click');
}
</script>

<style lang="scss">
.input-row--toggle {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs);
  background: transparent;
  color: variables.$primitives-color-tertiary-500;
  position: relative;

  .form-input--toggle {
    cursor: pointer;
    position: relative;
    user-select: none;

    label {
      @include mixins.flex-center($gap: variables.$sizing-xxxs);
      cursor: pointer;
    }

    .form-input--toggle--input {
      appearance: none;
      border: 1px solid transparent;
      border-radius: 6px;
      inset-block-start: 0;
      inset-inline-start: 0;
      cursor: pointer;
      height: 100%;
      margin: 0;
      opacity: 0;
      outline: 0 none;
      padding: 0;
      position: absolute;
      width: 100%;
      z-index: 1;
    }

    .form-input--toggle--box {
      background: variables.$primitives-color-tertiary-300;
      border: 1px solid variables.$primitives-color-tertiary-300;
      border-radius: variables.$sizing-lg;
      cursor: pointer;
      // Adding 2 to handle the border not cutting into the height.
      height: calc(variables.$sizing-lg + 2px);
      outline-color: transparent;
      transition: background 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out, outline-color 0.2s ease-in-out;
      // Adding 2 to handle the border not cutting into the width.
      width: calc(variables.$sizing-xxxl + 2px);
    }

    .form-input--toggle--handle {
      @include mixins.flex-center($horiz: center);
      background: variables.$primitives-color-background-100;
      border-radius: 50%;
      color: variables.$primitives-color-background-100;
      height: variables.$sizing-lg;
      inset-inline-start: 1px;
      position: absolute;
      top: 1px;
      transition: background 0.2s ease-in-out, color 0.2s ease-in-out, color 0.2s ease-in-out, inset-inline-start 0.2s ease-in-out;
      width: variables.$sizing-lg;
    }

    &.form-input--toggle--checked {
      .form-input--toggle--box {
        background: variables.$primitives-color-primary-500;
        border-color: variables.$primitives-color-primary-500;
      }

      .form-input--toggle--handle {
        inset-inline-start: calc(variables.$sizing-xxxl - calc(variables.$sizing-lg - 1px));
      }
    }

    &.form-input--toggle--disabled {
      .form-input--toggle--box,
      label {
        cursor: not-allowed;
      }

      .form-input--toggle--box {
        opacity: 0.5;
      }
    }
  }

  &:hover {
    .form-input--toggle:not(.form-input--toggle--disabled) {
      .form-input--toggle--handle {
        background: variables.$primitives-color-primary-500;
      }

      &.form-input--toggle--checked {
        .form-input--toggle--handle {
          background: variables.$primitives-color-tertiary-300;
        }
      }
    }
  }
}


</style>